import {
  useCallback,
  useContext, useEffect, useState,
} from 'react';
import useFetch from 'use-http';
import { debounce } from 'lodash-es';
import { Typography } from '@mui/material';
import AppContext from './App.context';
import {
  Dialog, Button, DialogActions, DialogContent,
} from '../designSystem';

const activityEvents = [
  'click', 'scroll', 'resetSessionTimeout',
];

const ONE_MIN_IN_MILLISECONDS = 60 * 1000;
const POPUP_WINDOW_TIME_IN_MS = 5 * ONE_MIN_IN_MILLISECONDS;
const LAST_ACTIVITY_TIMER = 10 * 1000;

export const SessionTimeoutWarning = () => {
  const {
    t, adminProperties,
  } = useContext(AppContext);

  // Ideal time before showing modal window. Defaults to 10 minutes if there is no adminProperties value.
  const sessionTimeoutMilliseconds = (
    (ONE_MIN_IN_MILLISECONDS * adminProperties?.adminProperties?.sessionTimeoutMin) - POPUP_WINDOW_TIME_IN_MS
  ) || (ONE_MIN_IN_MILLISECONDS * 10);

  const [apiCalledTime, setApiCalledTime] = useState(new Date().getTime());
  const [timeSinceLastActivity, setTimeSinceLastActivity] = useState(0);
  const [lastActionTimestamp, setLastActionTimestamp] = useState(new Date().getTime());

  const timeRemainingInMs = Math.max(POPUP_WINDOW_TIME_IN_MS - (timeSinceLastActivity - sessionTimeoutMilliseconds), 0);
  const timeRemainingInMinutes = Math.ceil(timeRemainingInMs / 60000);
  const minutesText = `minute${timeRemainingInMinutes === 1 ? '' : 's'}`;
  const showPopup = POPUP_WINDOW_TIME_IN_MS >= timeRemainingInMs;

  // We don't use/need the return value of this api,
  // but it still needs to be called every time apiCalledTime changes, because BE process its value.
  useFetch(`webcast/webapi/timeOutWarning/serverSessionTime?_=${apiCalledTime}`, {}, [apiCalledTime]);

  const updateLastActivityTime = useCallback(debounce((allowDuringPopup = false) => {
    // when popup is displayed then only reset activity by clicking on ok button
    if (!showPopup || allowDuringPopup) {
      setTimeSinceLastActivity(0);
      setLastActionTimestamp(new Date().getTime());
    }
  }, 500), [showPopup]);

  const handleOkButtonClicked = useCallback(() => {
    updateLastActivityTime(true);
    setApiCalledTime(new Date().getTime()); // call API to reset session
  }, [updateLastActivityTime]);

  useEffect(() => {
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateLastActivityTime, true);
    });
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateLastActivityTime, true);
      });
    };
  }, [updateLastActivityTime]);

  // timer to increment inactivity time by 10 seconds
  useEffect(() => {
    const timerId = setInterval(() => {
      const timePassed = new Date().getTime() - lastActionTimestamp;
      setTimeSinceLastActivity(timePassed);
    }, LAST_ACTIVITY_TIMER);
    return () => {
      window.clearInterval(timerId);
    };
  }, [lastActionTimestamp]);

  // update last api call time to current time, if user has been inactive to 1 min
  // or if user is active but its been 20 mins since last API called.
  useEffect(() => {
    const timeSinceLastApiCalled = new Date().getTime() - apiCalledTime;
    const twentyMinsInMS = 20 * 60 * 1000;
    if ((
      timeSinceLastActivity >= ONE_MIN_IN_MILLISECONDS
      && timeSinceLastActivity < (ONE_MIN_IN_MILLISECONDS + LAST_ACTIVITY_TIMER)
    )
    || (timeSinceLastActivity === 0 && timeSinceLastApiCalled > twentyMinsInMS)) {
      setApiCalledTime(new Date().getTime());
    }
  }, [timeSinceLastActivity]);

  // if user is inactive for more then 30 mins then redirect to logout page
  useEffect(() => {
    if (timeRemainingInMs <= 0) {
      window.location.href = '/webcast/logout';
    }
  }, [timeSinceLastActivity]);

  return (
    <Dialog
      title={t('SessionTimeout')}
      disableEscapeKeyDown
      show={showPopup}
      disableCloseIcon
      aria-labelledby="sessionTimeout"
      titleId="sessionTimeout"
    >
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography>{t('sessionTimeoutWarning', { timeRemaining: timeRemainingInMinutes, minutesText })}</Typography>
        <Typography>{t('clickOkToRemainLoggedIn')}</Typography>
      </DialogContent>
      <DialogActions align="right">
        <Button onClick={handleOkButtonClicked}>{t('SessionTimeoutOk')}</Button>
      </DialogActions>
    </Dialog>
  );
};
